import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { EmailAnalysisTabType } from "@types";
import { signOut } from "@store/auth";
import { getThreadTrackStatus, updateThreadTrackStatus } from "@store/signals";
import { AnalysisState } from "./analysis.types";
import { analyzeThread } from "./analysis.thunks";

const initialState: AnalysisState = {
  isLoading: {
    analyzeThread: false,
  },
  isError: {
    analyzeThread: false,
    permissionError: false,
    recipientsError: false,
  },
  activeTabId: EmailAnalysisTabType.Analysis,
  result: null,
  selectedBranch: null,
  selectedEmail: null,
  threadId: null,
  threadTrackStatus: null,
};

const analysisSlice = createSlice({
  initialState,
  name: "analysis",
  reducers: {
    selectBranch: (state, action: PayloadAction<string>) => {
      const selectedBranch =
        state.result?.branchResults?.find(({ branchId }) => branchId === action.payload) || null;
      const selectedBranchEmails = selectedBranch?.emails || [];

      state.selectedBranch = selectedBranch;
      state.selectedEmail = selectedBranchEmails[selectedBranchEmails.length - 1] || null;
    },
    selectEmail: (state, action: PayloadAction<string>) => {
      state.selectedEmail =
        state.selectedBranch?.emails?.find(({ messageId }) => messageId === action.payload) || null;
    },
    switchAnalysisTab: (state, action: PayloadAction<EmailAnalysisTabType>) => {
      state.activeTabId = action.payload;
    },
    updateThreadId: (state, action: PayloadAction<string>) => {
      state.threadId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(analyzeThread.pending, (state) => {
      state.isLoading.analyzeThread = true;
      state.isError.analyzeThread = false;
      state.isError.permissionError = false;
      state.isError.recipientsError = false;
    });
    builder.addCase(analyzeThread.fulfilled, (state, action) => {
      const { messageId } = action.meta.arg;
      const branchResults = action.payload.branchResults || [];
      const selectedBranch =
        branchResults?.find(
          ({ emails }) => !!emails?.find(({ platformMessageId }) => platformMessageId === messageId)
        ) || null;
      const selectedBranchEmails = selectedBranch?.emails || [];
      const selectedEmail =
        selectedBranchEmails.find(({ platformMessageId }) => platformMessageId === messageId) ||
        null;

      state.isLoading.analyzeThread = false;
      state.result = action.payload;
      state.selectedBranch = selectedBranch;
      state.selectedEmail = selectedEmail;
    });
    builder.addCase(analyzeThread.rejected, (state, action) => {
      const { permissionError = false, recipientsError = false } =
        (action.payload as { permissionError: boolean; recipientsError: boolean }) || {};
      state.isLoading.analyzeThread = false;
      state.isError.analyzeThread = !action.meta.aborted;
      state.isError.permissionError = permissionError;
      state.isError.recipientsError = recipientsError;
    });
    builder.addCase(getThreadTrackStatus.fulfilled, (state, action) => {
      state.threadTrackStatus =
        action.meta.arg.threadId === state.threadId ? action.payload.onTrack : null;
    });
    builder.addCase(updateThreadTrackStatus.pending, (state, action) => {
      state.threadTrackStatus =
        action.meta.arg.threadId === state.threadId ? action.meta.arg.onTrack : null;
    });
    builder.addCase(updateThreadTrackStatus.rejected, (state, action) => {
      state.threadTrackStatus = !action.meta.arg.onTrack;
    });
    builder.addCase(signOut.pending, (state) => {
      state.activeTabId = EmailAnalysisTabType.Analysis;
      state.result = null;
      state.selectedBranch = null;
      state.selectedEmail = null;
      state.threadId = null;
      state.threadTrackStatus = null;
    });
  },
});

export const { selectBranch, selectEmail, switchAnalysisTab, updateThreadId } =
  analysisSlice.actions;

export const analysisReducer = analysisSlice.reducer;
